import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ServiceAccordion from '../components/ServiceAccordion';
import ServiceDetails from '../components/ServiceDetails';
import ServiceSeo from '../components/ServiceSeo';
import NotFound from './NotFound';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import { getServiceRouteKey, hydrateService, hydrateServices, serviceDefaults } from '../utils/serviceContent';
import './Services.css';

export const servicesData = serviceDefaults;

const serviceMatchesKey = (service, key) => {
  const target = String(key || '');
  if (!target) return false;
  return [getServiceRouteKey(service), service?.slug, service?._id, service?.id, service?.legacyId]
    .filter(Boolean)
    .map(String)
    .includes(target);
};

const getDefaultService = (services = []) => (
  services.find((service) => Number(service?.legacyId) === 1)
  || services.find((service) => service?.slug === 'website-design')
  || services[0]
  || null
);

const Services = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { locale, localePath, setPageAlternates } = useLocale();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(Boolean(slug));
  const [notFound, setNotFound] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);
  const accordionRef = useRef(null);
  const previousOpenAccordionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setServicesLoading(true);

    api.get(`/api/services?locale=${encodeURIComponent(locale.code)}`)
      .then((response) => {
        if (cancelled) return;
        setServices(hydrateServices(response.data));
      })
      .catch((error) => {
        console.error(t('servicesPage.loadError'), error);
        if (!cancelled) setServices([]);
      })
      .finally(() => {
        if (!cancelled) setServicesLoading(false);
      });

    return () => { cancelled = true; };
  }, [locale.code, t]);

  // `/services` is only an entry route. Preserve the previous UX by opening
  // website design immediately, while every service still has its own clean URL.
  // The production server performs the same redirect with HTTP 301; this client
  // redirect covers in-app navigation where React Router handles the click.
  useEffect(() => {
    if (slug || servicesLoading || !services.length) return;

    const legacyKey = searchParams.get('service');
    const targetService = legacyKey
      ? services.find((service) => serviceMatchesKey(service, legacyKey))
      : getDefaultService(services);
    const fallbackService = targetService || getDefaultService(services);

    if (!fallbackService?.slug) return;
    navigate(localePath(`/services/${fallbackService.slug}${location.hash || ''}`), { replace: true });
  }, [location.hash, localePath, navigate, searchParams, services, servicesLoading, slug]);

  useEffect(() => {
    if (!slug) {
      setSelectedService(null);
      setDetailLoading(false);
      setNotFound(false);
      setPageAlternates(null);
      return undefined;
    }

    if (servicesLoading) return undefined;

    const localService = services.find((service) => serviceMatchesKey(service, slug));
    if (localService) {
      // Keep the old instant switching behavior: the localized services list
      // already contains the full page content, so no loading screen is needed
      // when the user moves between service cards.
      setSelectedService(localService);
      setDetailLoading(false);
      setNotFound(false);
    } else {
      setDetailLoading(true);
      setNotFound(false);
    }

    let cancelled = false;
    api.get(`/api/services/${encodeURIComponent(slug)}?locale=${encodeURIComponent(locale.code)}`)
      .then((response) => {
        if (cancelled || !response.success || !response.data) return;

        const service = hydrateService(response.data);
        setSelectedService(service);
        setNotFound(false);
        setPageAlternates(Object.fromEntries(
          Object.entries(response.data.alternates || {}).map(([code, alternateSlug]) => [
            code,
            `/${code}/services/${alternateSlug}`,
          ])
        ));

        // Source slugs, legacy identifiers and changed slugs remain usable, but
        // the browser always settles on the localized canonical service URL.
        if (service.slug && service.slug !== slug) {
          navigate(localePath(`/services/${service.slug}${location.hash || ''}`), { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled && !localService) {
          setSelectedService(null);
          setNotFound(true);
          setPageAlternates(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => { cancelled = true; };
  }, [locale.code, localePath, location.hash, navigate, services, servicesLoading, setPageAlternates, slug]);

  const activeServiceKey = useMemo(
    () => selectedService ? getServiceRouteKey(selectedService) : '',
    [selectedService]
  );

  const handleServiceClick = () => {
    setOpenAccordion(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (window.location.hash !== '#pricing-plans' || !selectedService?.planGroups?.length) return;
    const timer = setTimeout(() => {
      const section = document.getElementById('pricing-plans');
      if (!section) return;
      const top = section.getBoundingClientRect().top + window.pageYOffset - 270;
      window.scrollTo({ top, behavior: 'smooth' });
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }, 800);
    return () => clearTimeout(timer);
  }, [selectedService]);

  useEffect(() => {
    if (openAccordion && openAccordion !== previousOpenAccordionRef.current && accordionRef.current) {
      const timer = setTimeout(() => accordionRef.current?.scrollToItem(openAccordion), 450);
      previousOpenAccordionRef.current = openAccordion;
      return () => clearTimeout(timer);
    }
    previousOpenAccordionRef.current = openAccordion;
  }, [openAccordion]);

  if (servicesLoading || !slug || detailLoading) {
    return (
      <div className="services-page">
        <div className="services-loading" role="status">{t('servicesPage.loading')}</div>
      </div>
    );
  }

  if (notFound || !selectedService) return <NotFound />;

  return (
    <div className="services-page">
      <ServiceSeo service={selectedService} />

      <div className="services-header">
        <ServiceAccordion
          services={services}
          activeService={activeServiceKey}
          onServiceClick={handleServiceClick}
        />
      </div>

      <main className="services-container">
        <ServiceDetails
          service={selectedService}
          openAccordion={openAccordion}
          onToggleAccordion={(id) => setOpenAccordion(openAccordion === id ? null : id)}
          accordionRef={accordionRef}
        />
      </main>
    </div>
  );
};

export default Services;
